import { type SetupStep3Input, setupStep3Schema } from '@app/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useAuthStore } from '../../auth/store';
import { DemoPrefillButton } from '../../demo/components/DemoPrefillButton';
import { initializeSetup } from '../api';
import { useSetupWizardStore } from '../store/setup-wizard.store';

interface Props {
  onCompleted: () => void;
}

export function SetupStep3Form({ onCompleted }: Props) {
  const { step1, step2, step3, setStep, setStep3, reset } = useSetupWizardStore();
  const setSession = useAuthStore((s) => s.setSession);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SetupStep3Input>({
    resolver: zodResolver(setupStep3Schema),
    defaultValues: {
      sites:
        step3.sites && step3.sites.length > 0
          ? step3.sites
          : [{ name: '', addressLine1: '', postalCode: '', city: '', country: '' }],
      preferredSiteIndex: step3.preferredSiteIndex ?? 0,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'sites' });
  const preferredSiteIndex = watch('preferredSiteIndex');

  return (
    <form
      className="space-y-6"
      noValidate
      onSubmit={handleSubmit(async (data) => {
        if (!step1.alias || !step2.name) {
          setSubmitError('Étapes précédentes incomplètes');
          return;
        }
        setStep3(data);
        setSubmitError(null);
        try {
          const res = await initializeSetup({
            admin: step1 as Required<typeof step1>,
            company: step2 as Required<typeof step2>,
            step3: data,
          });
          setSession(res.accessToken, res.user);
          reset();
          onCompleted();
        } catch (err) {
          setSubmitError(err instanceof Error ? err.message : 'Échec de l’initialisation');
        }
      })}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">
            Étape 3 — Sites de l’entreprise
          </h2>
          <p className="mt-2 text-[0.9rem] leading-relaxed text-slate-600">
            Ajoutez au moins un site. Cochez « Je travaille ici » sur celui qui vous concerne.
          </p>
        </div>
        <DemoPrefillButton
          context="setup-step3"
          onPrefill={(data) => {
            const sitesData = data.sites as SetupStep3Input['sites'] | undefined;
            const preferred = data.preferredSiteIndex as number | undefined;
            if (sitesData) setValue('sites', sitesData, { shouldDirty: true });
            if (typeof preferred === 'number') {
              setValue('preferredSiteIndex', preferred, { shouldDirty: true });
            }
          }}
        />
      </div>

      <div className="space-y-4">
        {fields.map((field, idx) => (
          <div
            key={field.id}
            className="rounded-2xl border border-slate-200 bg-white/70 p-5 ring-1 ring-slate-100"
          >
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-semibold tracking-tight text-slate-700">Site {idx + 1}</p>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="preferred-site"
                    checked={preferredSiteIndex === idx}
                    onChange={() => setValue('preferredSiteIndex', idx)}
                  />
                  Je travaille ici
                </label>
                {fields.length > 1 && (
                  <button
                    type="button"
                    className="text-sm text-rose-600 hover:underline"
                    onClick={() => remove(idx)}
                  >
                    Supprimer
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="app-label" htmlFor={`site-${idx}-name`}>
                  Nom du site
                </label>
                <input
                  id={`site-${idx}-name`}
                  className="app-input"
                  {...register(`sites.${idx}.name`)}
                />
                {errors.sites?.[idx]?.name && (
                  <p className="text-error mt-2">{errors.sites[idx]?.name?.message}</p>
                )}
              </div>
              <div>
                <label className="app-label" htmlFor={`site-${idx}-address`}>
                  Adresse
                </label>
                <input
                  id={`site-${idx}-address`}
                  className="app-input"
                  {...register(`sites.${idx}.addressLine1`)}
                />
              </div>
              <div>
                <label className="app-label" htmlFor={`site-${idx}-postal`}>
                  Code postal
                </label>
                <input
                  id={`site-${idx}-postal`}
                  className="app-input"
                  {...register(`sites.${idx}.postalCode`)}
                />
              </div>
              <div>
                <label className="app-label" htmlFor={`site-${idx}-city`}>
                  Ville
                </label>
                <input
                  id={`site-${idx}-city`}
                  className="app-input"
                  {...register(`sites.${idx}.city`)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="app-label" htmlFor={`site-${idx}-country`}>
                  Pays
                </label>
                <input
                  id={`site-${idx}-country`}
                  className="app-input"
                  {...register(`sites.${idx}.country`)}
                />
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn-muted"
          onClick={() =>
            append({ name: '', addressLine1: '', postalCode: '', city: '', country: '' })
          }
        >
          Ajouter un site
        </button>
      </div>

      {submitError && (
        <p className="text-error rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          {submitError}
        </p>
      )}

      <div className="flex justify-between gap-4">
        <button type="button" className="btn-muted" onClick={() => setStep(2)}>
          Retour
        </button>
        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Initialisation…' : 'Terminer le setup'}
        </button>
      </div>
    </form>
  );
}
